import React, { useState } from "react";

/**
 * The pitch-lifecycle rule, as a collapsible "i".
 *
 * ONLY THE PRESENTATION CHANGED (Marin, 2026-08-01: "Wir erweitern, wir bauen
 * nichts um was funktioniert"; handoff section 1: "Jetzt einzeilig einklappbar
 * […] 3 Absätze im Aufklapper"). The three sentences below are the WP2 copy
 * word for word, with one edit: the em-dash in the scouting rule is now a full
 * stop, because the V2 copy rules forbid em-dashes.
 *
 * V1 kept all three paragraphs open at all times. Measured, that box was ~480px
 * of the ~470px that already stood between a 375px screen and the first pitch
 * (inventory 7.6): the club scrolled past the same rule on every tab, every
 * visit. The rule is still plain visible text, never a tooltip — the headline
 * states the deadline outright and one tap opens the three rules underneath.
 *
 * Every string is plain English on purpose: the platform translates RENDERED
 * text (contexts/TranslationContext.js), so the English sentence IS the key.
 */
const PitchLifecycleHint = ({ isStaffRequest = false }) => {
  const [open, setOpen] = useState(false);

  const rules = [
    {
      key: "respond",
      title: "96 hours to respond",
      // WP2 copy, unchanged.
      body:
        "A pitch you leave untouched expires when its time runs out, and the agent is told you are not interested for now.",
      circle: "bg-[#DBEAFE]",
      titleColor: "text-[#1E40AF]",
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="6" stroke="#1D4ED8" strokeWidth="1.6" />
          <path d="M8 4.8V8L10.2 9.6" stroke="#1D4ED8" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      key: "scouting",
      title: "Scouting adds 3 days",
      // WP2 copy, with the em-dash replaced by a full stop (V2 copy rules) and
      // the staff wording that WP2 never had, because the same box is shown on
      // the staff and coach routes.
      body: isStaffRequest
        ? "You can send a pitch to scouting up to 3 times. Each click gives you 3 more days to look at the candidate."
        : "You can send a pitch to scouting up to 3 times. Each click gives you 3 more days to look at the player.",
      circle: "bg-[#FEF3C7]",
      titleColor: "text-[#92400E]",
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="4.2" stroke="#B45309" strokeWidth="1.6" />
          <path d="M10.2 10.2L13 13" stroke="#B45309" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      key: "pickup",
      title: "14 days to pick up an expired pitch",
      // WP2 copy, unchanged.
      body:
        "An expired pitch stays in your list for 14 days and you can still pick it up in that time. After that it is rejected automatically and the agent is informed.",
      circle: "bg-[#F3F4F6]",
      titleColor: "text-[#374151]",
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M13 8C13 10.8 10.8 13 8 13C5.2 13 3 10.8 3 8C3 5.2 5.2 3 8 3C9.9 3 11.5 4 12.4 5.5"
            stroke="#4B5563"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <path
            d="M12.6 2.8V5.6H9.8"
            stroke="#4B5563"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-w-0 flex-[1_1_320px] max-w-[780px] rounded-xl border border-[#D3E5FE] bg-[#EFF6FF]">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full min-h-[44px] items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left"
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 16 16"
          fill="none"
          className="flex-shrink-0"
          aria-hidden="true"
        >
          <circle cx="8" cy="8" r="7" stroke="#2183FE" strokeWidth="1.5" />
          <path d="M8 7.2V11" stroke="#2183FE" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="8" cy="5" r="0.9" fill="#2183FE" />
        </svg>
        <span className="min-w-0 flex-1 text-[14px] font-semibold text-[#1D4ED8]">
          You have 96 hours to decide on each pitch
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className={`flex-shrink-0 text-[#1D4ED8] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        <div className="flex flex-col gap-2.5 px-4 pb-4 pt-0.5">
          {rules.map((rule) => (
            <div
              key={rule.key}
              className="flex items-start gap-3 rounded-[10px] border border-[#D3E5FE] bg-white px-3.5 py-3"
            >
              <span
                className={`flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full ${rule.circle}`}
              >
                {rule.icon}
              </span>
              <span className="min-w-0">
                <span className={`block text-[14px] font-bold ${rule.titleColor}`}>
                  {rule.title}
                </span>
                <span className="mt-0.5 block text-[13px] leading-relaxed text-[#374151]">
                  {rule.body}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default PitchLifecycleHint;
